import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShareIcon } from '@/ui/icons';
import { IconButton } from '@/ui/icon-button/icon-button';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@/ui/layout';
import { Button } from '@/ui/button/button';
import { TextField } from '@/ui/text-field/text-field';
import { useStoreGeoViewMapId, useStoreGeoViewSharedMode } from '@/core/stores/geoview-store';
import {
  useStoreMapInteraction,
  useStoreMapZoom,
  useStoreMapCenterCoordinates,
  useStoreMapCurrentProjection,
  useStoreMapCurrentBasemapOptions,
} from '@/core/stores/states/map-state';
import { useStoreLayerOrderedLayerPaths } from '@/core/stores/states/layer-state';
import { Projection } from '@/geo/utils/projection';

import type { Coordinate } from 'ol/coordinate';
import type { TypeBasemapOptions, TypeValidMapProjectionCodes } from '@/api/types/map-schema-types';
import { logger } from '@/core/utils/logger';
import { isValidUUID } from '@/core/utils/utilities';

/**
 * Generates a shareable URL query string from the current map state.
 *
 * This function creates URL parameters that can be used with maps having the data-shared attribute.
 * When a map with data-shared and data-config (or data-config-url) loads with these URL parameters,
 * the base configuration is preserved and URL parameters selectively update specific properties:
 * - Projection, zoom, and center coordinates are updated
 * - Basemap options (basemapId, shaded, labeled) are updated
 * - Layers from URL parameters are appended to existing config layers (not replaced)
 *
 * @param zoom - Current zoom level of the map
 * @param center - Current center coordinates of the map in the map's projection
 * @param projection - Current projection code (e.g., 3857, 3978, ..)
 * @param basemap - Current basemap configuration (basemapId, shaded, labeled)
 * @param layers - Array of layer paths; UUIDs are extracted from paths (e.g., "uuid/sublayer" -> "uuid")
 * @returns URL query string with format: "?p=3857&z=4&c=-100.123456,40.654321&b=id:transport,s:on,l:off&keys=uuid1,uuid2".
 *          Center coordinates are automatically reprojected to lon/lat (EPSG:4326) for sharing.
 *          Returns empty string if an error occurs during generation
 */
function getShareUrl(
  zoom: number,
  center: Coordinate,
  projection: TypeValidMapProjectionCodes,
  basemap: TypeBasemapOptions,
  layers: string[]
): string {
  try {
    const params: string[] = [];

    // Set projection (p)
    params.push(`p=${projection}`);

    // Set current zoom (z)
    params.push(`z=${zoom}`);

    // Set current center (c) - format: lon,lat (reprojected to EPSG:4326)
    const lonLat = Projection.transformToLonLat(center, Projection.getProjectionFromStringOrNumber(projection));
    params.push(`c=${lonLat[0].toFixed(6)},${lonLat[1].toFixed(6)}`);

    // Get basemap options (b) - format: id:value,s:on/off,l:on/off
    const basemapParts: string[] = [];
    basemapParts.push(`id:${basemap.basemapId}`);
    basemapParts.push(`s:${basemap.shaded ? 'on' : 'off'}`);
    basemapParts.push(`l:${basemap.labeled ? 'on' : 'off'}`);
    params.push(`b=${basemapParts.join(',')}`);

    // Get layer keys (keys) - Process layer paths to extract valid UUIDs
    if (layers && layers.length > 0) {
      // Extract UUIDs from layer paths (split by '/' and take first part)
      const validUUIDs = layers
        .map((layer) => layer.split('/')[0]) // Get the first part before '/'
        .filter((id) => id && isValidUUID(id)) // Keep only valid UUIDs
        .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

      params.push(`keys=${validUUIDs.join(',')}`);
    }

    // Return the query string
    return params.length > 0 ? `?${params.join('&')}` : '';
  } catch (error: unknown) {
    logger.logError('Error generating share URL:', error);
    return '';
  }
}

/**
 * Share button component that allows users to copy a shareable URL based on the current map state.
 *
 * Only visible when the map has data-shared attribute.
 *
 * @returns The share button and dialog, or null if shared mode is not enabled
 */
export default function Share(): JSX.Element | null {
  // Log
  logger.logTraceRender('components/app-bar/buttons/share');

  // Hooks
  const { t } = useTranslation<string>();

  // Store
  const mapId = useStoreGeoViewMapId();
  const sharedMode = useStoreGeoViewSharedMode();
  const interaction = useStoreMapInteraction();
  const zoom = useStoreMapZoom();
  const center = useStoreMapCenterCoordinates();
  const projection = useStoreMapCurrentProjection();
  const basemap = useStoreMapCurrentBasemapOptions();
  const layers = useStoreLayerOrderedLayerPaths();

  // State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [shareUrl, setShareUrl] = useState<string>('');

  // #region Handlers

  /**
   * Handles when the share button is clicked.
   */
  const handleShareClick = useCallback((): void => {
    // Generate the URL only when opening the modal
    const baseUrl = window.location.origin + window.location.pathname;
    const queryParams = getShareUrl(zoom, center, projection, basemap, layers);
    setShareUrl(baseUrl + queryParams);

    setIsModalOpen(true);
  }, [zoom, center, projection, basemap, layers]);

  /**
   * Handles when the modal is closed.
   */
  const handleCloseModal = useCallback((): void => {
    setIsModalOpen(false);
  }, []);

  /**
   * Handles copying the URL to clipboard.
   */
  const handleCopyUrl = useCallback((): void => {
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        logger.logInfo('URL copied to clipboard:', shareUrl);
        // Close modal after successful copy
        setIsModalOpen(false);
        // TODO: Show success notification
      },
      (error: unknown) => {
        logger.logError('Failed to copy URL to clipboard', error);
      }
    );
  }, [shareUrl]);

  // #endregion

  // If shared mode is not enabled, don't render the button
  const isVisible = sharedMode === true;
  if (!isVisible) return null;

  return (
    <>
      <IconButton
        id={`${mapId}-share-button`}
        aria-label={t('appbar.share')}
        tooltip={t('appbar.share')}
        tooltipPlacement="right"
        onClick={handleShareClick}
        className={interaction === 'dynamic' ? 'buttonFilled' : 'appBarButtonStatic'}
      >
        <ShareIcon />
      </IconButton>

      <Dialog open={isModalOpen} onClose={handleCloseModal} aria-labelledby="share-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="share-dialog-title">{t('appbar.share')}</DialogTitle>
        <DialogContent dividers>
          <Box>
            <TextField
              fullWidth
              value={shareUrl}
              slotProps={{
                input: {
                  readOnly: true,
                },
                inputLabel: {
                  sx: {
                    color: 'primary.main',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                },
              }}
              label={t('appbar.shareUrl')}
              variant="outlined"
              multiline
              maxRows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyUrl} type="text" variant="contained" color="primary" autoFocus>
            {t('general.copy')}
          </Button>
          <Button onClick={handleCloseModal} type="text">
            {t('general.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
